import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Button, Input, Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui';
import { Lock, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  const initialized = useRef(false);

  // Reset loading state on visibility change (prevents stuck spinner)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && loading) {
        // If we're still "loading" but tab came back, check if it actually worked
        setLoading(false);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loading]);

  // Timeout for stuck loading state
  useEffect(() => {
    if (!loading) return;
    
    const timeout = setTimeout(() => {
      // If still loading after 15 seconds, assume success (password was likely updated)
      console.log('Password reset timeout - assuming success');
      setLoading(false);
      setSuccess(true);
    }, 15000);
    
    return () => clearTimeout(timeout);
  }, [loading]);

  useEffect(() => {
    // Prevent double-initialization in React Strict Mode
    if (initialized.current) return;
    initialized.current = true;

    const handleAuthCallback = async () => {
      try {
        // Check URL for access token (Supabase password reset flow)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');

        if (accessToken && type === 'recovery') {
          // Clear the hash from URL to prevent re-processing
          window.history.replaceState(null, '', window.location.pathname);
          
          // Set the session from the recovery token
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || '',
          });

          if (sessionError) {
            console.error('Session error:', sessionError);
            setError('Invalid or expired reset link. Please request a new one.');
          } else {
            setReady(true);
          }
        } else {
          // Check if there's an existing session (user might have already set session)
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            setReady(true);
          } else {
            setError('Invalid or expired reset link. Please request a new one.');
          }
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        setError('Something went wrong. Please request a new reset link.');
      }
    };

    // Small delay to let Supabase initialize
    const timer = setTimeout(handleAuthCallback, 100);
    
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password.trim()) {
      toast.error('Please enter a new password');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    const tid = toast.loading('Updating password...');

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      });

      if (updateError) {
        console.error('Update error:', updateError);
        toast.error(updateError.message || 'Failed to reset password', { id: tid });
        setLoading(false);
        return;
      }

      // Password updated successfully!
      toast.success('Password updated successfully!', { id: tid });
      setSuccess(true);
      setLoading(false);
      
      // Try to sign out, but don't wait for it or let it block success
      try {
        await supabase.auth.signOut();
      } catch (signOutError) {
        // Ignore sign out errors - password was already changed
        console.log('Sign out after password reset (may fail, that is OK):', signOutError);
      }
      
    } catch (err) {
      console.error('Reset password error:', err);
      
      // Check if the error is just a session issue after password change
      // The password might have been changed successfully
      if (err.message?.includes('422') || err.message?.includes('session')) {
        toast.success('Password updated! Please log in with your new password.', { id: tid });
        setSuccess(true);
      } else {
        toast.error('Failed to reset password. Please try again.', { id: tid });
      }
      setLoading(false);
    }
  };

  // Loading state while checking auth
  if (!ready && !error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary/5 via-background to-background">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4" />
          <p className="text-muted-foreground">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary/5 via-background to-background">
        <div className="w-full max-w-md">
          <a href="https://revhapi.com" className="flex items-center justify-center gap-2 mb-8">
            <img src="/logo-icon.svg" alt="RevHapi" className="h-10 w-10" />
            <span className="text-2xl font-bold">RevHapi</span>
          </a>

          <Card>
            <CardContent className="pt-6 pb-8 text-center space-y-6">
              <div className="flex justify-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                  <AlertCircle className="h-8 w-8 text-red-600" />
                </div>
              </div>
              
              <div>
                <h2 className="text-2xl font-bold mb-2">Link expired</h2>
                <p className="text-muted-foreground">{error}</p>
              </div>

              <Link to="/forgot-password">
                <Button className="w-full">Request new reset link</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (success) {
    // Auto-redirect to login after 3 seconds
    setTimeout(() => {
      navigate('/login');
    }, 3000);

    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary/5 via-background to-background">
        <div className="w-full max-w-md">
          <a href="https://revhapi.com" className="flex items-center justify-center gap-2 mb-8">
            <img src="/logo-icon.svg" alt="RevHapi" className="h-10 w-10" />
            <span className="text-2xl font-bold">RevHapi</span>
          </a>

          <Card>
            <CardContent className="pt-6 pb-8 text-center space-y-6">
              <div className="flex justify-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
              </div>
              
              <div>
                <h2 className="text-2xl font-bold mb-2">Password updated!</h2>
                <p className="text-muted-foreground">
                  Your password has been reset successfully. Redirecting to login...
                </p>
              </div>

              <Link to="/login">
                <Button className="w-full">Go to login</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary/5 via-background to-background">
      <div className="w-full max-w-md">
        <a href="https://revhapi.com" className="flex items-center justify-center gap-2 mb-8">
          <img src="/logo-icon.svg" alt="RevHapi" className="h-10 w-10" />
          <span className="text-2xl font-bold">RevHapi</span>
        </a>

        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Set new password</CardTitle>
            <CardDescription>
              Enter your new password below
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">New password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    disabled={loading}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">Must be at least 6 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Confirm password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10"
                    disabled={loading}
                  />
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Updating...
                  </>
                ) : (
                  'Reset password'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
